/**
 * Meridian BFF API client.
 *
 * Thin fetch wrapper that:
 *
 * - Injects the CSRF header on mutating requests (POST / PUT /
 *   PATCH / DELETE) per the BFF's :class:`CsrfMiddleware`.
 * - Always sends credentials so the session cookie rides along
 *   on every request (the BFF's :class:`SessionMiddleware` reads
 *   it).
 * - Maps 401 → ``redirectToLogin()`` (full-page navigation to
 *   ``/api/auth/login`` so the BFF runs the PKCE flow).
 * - Maps 403 → router navigation to ``/403``.
 * - Surfaces other errors as ``ApiError`` for callers to handle.
 *
 * Typed responses come from ``api-types.gen.ts`` (regenerated by
 * ``npm run codegen`` from ``packages/meridian_bff/api/openapi.yaml``).
 * Callers cast the JSON return as needed — full openapi-fetch
 * integration lands in a later PR alongside the snapshot/trail
 * surfaces.
 *
 * Cross-references:
 *   - REQ-AUTH-022 (BFF-side RBAC; 401/403 mapping mirrors the
 *     BFF surface).
 *   - REQ-UI-035 (no external network calls — only ``/api/*``).
 *   - REQ-UI-037 (no internal-error leakage in user-facing
 *     messages — ``ApiError.message`` is intentionally generic).
 */
import { CSRF_HEADER_NAME, CSRF_HEADER_VALUE } from "@/lib/csrf";
import { clearAllDefaultAppliedMarkers } from "@/preferences/default-applied-marker";

const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export interface ApiRequestOptions extends RequestInit {
  /** When true (default), 401 triggers a full-page redirect to
   *  ``/api/auth/login``. Set false for the bootstrap fetch
   *  itself so the SessionProvider can render the login page
   *  in-place instead of looping. */
  redirectOnUnauthenticated?: boolean;
  /** When true, resolve with the raw :class:`Response` instead of parsed
   *  JSON. For routes that answer with a file (the ADR-038 result export),
   *  where the caller needs the body as a Blob but still wants this
   *  client's CSRF header, credential handling, and error mapping. */
  rawResponse?: boolean;
}

/**
 * Issue a request against the BFF surface.
 *
 * ``path`` is appended to the document origin; absolute URLs are
 * rejected (REQ-UI-035 — the UI must not make external network
 * calls).
 */
export async function apiFetch<T = unknown>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  if (/^[a-z]+:\/\//i.test(path)) {
    throw new ApiError(0, null, "apiFetch: absolute URLs are not permitted (REQ-UI-035)");
  }
  const method = (options.method ?? "GET").toUpperCase();
  const headers = new Headers(options.headers ?? {});

  // Presence-only CSRF: the BFF requires the header on every
  // mutating request; the fixed sentinel value is what cross-
  // origin forms cannot set. Cookie state is irrelevant. See
  // ``packages/meridian_bff/src/meridian_bff/_csrf.py`` docstring
  // lines 7-27.
  if (MUTATING_METHODS.has(method)) {
    headers.set(CSRF_HEADER_NAME, CSRF_HEADER_VALUE);
  }

  // The BFF expects JSON request bodies for its mutating routes;
  // ensure callers that supply a string / object body have the
  // header set. Binary uploads (CSV bulk-import) override
  // explicitly.
  if (options.body && !headers.has("Content-Type")) {
    if (typeof options.body === "string") {
      headers.set("Content-Type", "application/json");
    }
  }

  const response = await fetch(path, {
    ...options,
    method,
    headers,
    credentials: "include",
  });

  if (response.status === 401 && options.redirectOnUnauthenticated !== false) {
    redirectToLogin();
    // ``redirectToLogin`` triggers a full-page navigation; the
    // returned promise never resolves in production. Throw so
    // tests that exercise the redirect path don't hang on a
    // never-resolving fetch.
    throw new ApiError(401, null, "unauthenticated");
  }

  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      // Non-JSON error body; leave as null.
    }
    throw new ApiError(response.status, body, response.statusText || `HTTP ${response.status}`);
  }

  // File-returning routes take the Response itself; parsing it as JSON
  // would consume the body the caller needs.
  if (options.rawResponse === true) {
    return response as T;
  }

  // 204 No Content -> resolve with undefined; callers typing on
  // ``T = void`` see the expected shape.
  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/**
 * Full-page navigation to the BFF login endpoint. The BFF then
 * runs the OIDC PKCE flow against Keycloak and 302s back to ``/``
 * with the session cookie set.
 */
export function redirectToLogin(): void {
  if (typeof window === "undefined") {
    return;
  }
  // Preserve the current path so the post-login landing can
  // restore it. Encoded into the state cookie's ``landing_path``
  // claim per BFF PR A.
  const target = new URL("/api/auth/login", window.location.origin);
  target.searchParams.set("redirect_to", window.location.pathname + window.location.search);
  window.location.assign(target.toString());
}

/**
 * Log out — clears the BFF session and reloads to the login page.
 *
 * Also clears the per-user fresh-login markers
 * (:func:`clearAllDefaultAppliedMarkers`) so the next sign-in is treated as a
 * fresh login and re-applies the user's chosen login-default Profile (ADR-033
 * §3.2). The clear runs before the reload so it lands regardless of the
 * logout-request outcome.
 */
export async function logout(): Promise<void> {
  // Clear the fresh-login markers first — the next sign-in (any user) must
  // re-resolve their login-default Profile (ADR-033 §3.2). Best-effort and
  // synchronous; unaffected by the BFF logout call's success.
  clearAllDefaultAppliedMarkers();
  try {
    await apiFetch("/api/auth/logout", { method: "POST", redirectOnUnauthenticated: false });
  } catch {
    // Best-effort: even if the BFF rejected the logout (already
    // logged out, network blip), reload to clear in-memory state.
  }
  if (typeof window !== "undefined") {
    window.location.assign("/");
  }
}
